import React, { useEffect, useRef } from 'react';
import { cn } from '../../../lib/utils';
import { useViewport } from '../../../contexts/ViewportContext';

interface TaskFormTitleProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
  isEditMode?: boolean;
  autoFocus?: boolean;
  className?: string;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
}

export const TaskFormTitle: React.FC<TaskFormTitleProps> = ({
  value,
  onChange,
  onKeyDown,
  isEditMode = false,
  autoFocus = false,
  className = '',
  inputRef,
}) => {
  const { isMobile } = useViewport();
  const internalTitleRef = useRef<HTMLTextAreaElement>(null);
  const titleRef = inputRef || internalTitleRef;

  // Auto-adjust height for title textarea in edit mode - plus immediate adjustment on mount
  useEffect(() => {
    if (isEditMode && titleRef.current) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
    }
  }, [isEditMode, value]);

  // Auto-adjust height for title textarea in new task mode
  useEffect(() => {
    if (titleRef.current && !isEditMode) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = `${titleRef.current.scrollHeight}px`;
    }
    // We intentionally don't include inputRef in dependencies as it doesn't change identity
  }, [isEditMode, value]);

  return (
    <div className={cn(isEditMode ? 'mb-5' : '', isMobile && isEditMode && 'mt-3', className)}>
      <textarea
        ref={titleRef}
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={isEditMode ? 'Task title' : 'What needs to be done?'}
        className={cn(
          'w-full bg-transparent text-gray-900 placeholder-gray-500 focus:outline-none font-montserrat p-0 resize-none overflow-hidden min-h-[28px]',
          isEditMode ? 'font-semibold text-xl' : 'pt-[6px] pb-[1px] title-input',
          isMobile && !isEditMode && 'text-lg'
        )}
        autoFocus={autoFocus}
        required
        rows={1}
        style={{
          overflow: 'hidden',
          height: 'auto',
        }}
      />
    </div>
  );
};
